import { cloneDeep, isEmpty } from 'lodash';
import { ValueField } from '../../settings/model/value-field';
import { IconMenuCreator } from '../util/icon-menu-creator';
import { ColumnDefinition } from './column-definition';
import { CustomValidator } from './custom-validator';
import { Operator } from './operator';
import { RequestCriteria } from './request-criteria';
import { TableElementFormatter, TableElementFormatterCollection } from './table-element-formatter';
import { Sort, SortDirection } from './transformations';
import { SortMeta } from 'primeng/api';

export class DataSource {
  name: string;
  fields: DataSourceField[];
  initialFields: DataSourceField[];
  customValidators: CustomValidator[];
  initialFilters: RequestCriteria;
  sortBy?: SortBy[];
  groupBy?: string;
  tableRowFormatter?: TableElementFormatter<any>;
  tableCellFormatters: TableElementFormatterCollection<any> = {};
  iconMenuCreator?: IconMenuCreator<any>;
  optionsCellCreator?: IconMenuCreator<any>;
  /** "Export all" fetch chunk size for this table. Unset/null = fetch everything in a single request (legacy behavior). */
  exportPageSize?: number | null;
  /** Hard cap on rows an "export all" is allowed to fetch for this table. Unset/null = service default (see DEFAULT_MAX_EXPORT_ROWS). */
  maxExportRows?: number | null;
  /** When it returns true for the export's current filters, maxExportRows is skipped entirely for that export. */
  allowExceedMaxExportRows?: (criteria: RequestCriteria) => boolean;

  constructor(name: any, fields: DataSourceField[]) {
    this.name = name.toString();
    this.fields = fields;
    this.initialFields = cloneDeep(fields);
  }

  getColumns(): ColumnDefinition[] {
    return this.fields.map(field => field.column);
  }

  resetFieldsToInit(): void {
    this.fields = cloneDeep(this.initialFields);
  }

  syncDataSourceInitialColumnVisablity(columnName: string, isVisible: boolean): void {
    const field = this.initialFields.find(v => v.column.name === columnName || v.valueField.fieldName === columnName) ?? null;
    if (field != null) {
      field.valueField.visible = isVisible;
    }
  }

  getValueFields(): ValueField[] {
    return this.fields.map(field => field.valueField);
  }

  getMutatorFields(): DataSourceField[] {
    return this.fields.filter(field => field.column.mutator);
  }

  setInitialFilters(criteria: RequestCriteria[]): void {
    this.initialFilters = {
      operator: Operator.and,
      criteria
    };
  }

  setTableRowFormatter(formatter: TableElementFormatter<any>): void {
    this.tableRowFormatter = formatter;
  }

  setIconMenuCreator(creator: IconMenuCreator<any>): void {
    this.iconMenuCreator = creator;
  }

  setOptionsCellCreator(creator: IconMenuCreator<any>): void {
    this.optionsCellCreator = creator;
  }

  addTableCellFormatter(field: string, formatter: TableElementFormatter<any>): void {
    this.tableCellFormatters[field] = formatter;
  }

}

export interface DataSourceField {
  column: ColumnDefinition;
  valueField: ValueField;
}

export class ColumnVisabilityPositionSetting {
  constructor(public columnName: string, public isVisible: boolean, public position: number, public width: any, public title?: string) { }
}

export class SortBy {

  constructor(public property: string, public direction: 'asc' | 'desc') {
    if (isEmpty(property) || isEmpty(direction))
      throw new Error('empty datasource sort input!!!');
  }

  toSmartGwtSort(): string {
    return (this.direction === 'desc' ? '-' : '') + this.property;
  }

  static createFromTransformationsSort(sort: Sort): SortBy {
    return new SortBy(sort.active, sort.direction === SortDirection.ASC ? 'asc' : 'desc');
  }

  toJsonString(): string {
    const sort = this.direction === 'asc' ? 'ascending' : 'descending';
    return `{"property":"${this.property}","direction":"${sort}"}`;
  }

  toMultiSortMeta(): SortMeta {
    return { field: this.property, order: this.direction === 'asc' ? 1 : -1 };
  }

}

